import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import { EditQuestion, QuestionDetails } from "../../../services/question.services";
import { SWAL_SETTINGS, questionType } from "../../../../../utils/Constants";
import { globalLoader, handleServerValidations } from "../../../../../utils/commonfunction";
import { useTranslation } from "react-i18next";
import Loader from "../../../common/loader";

const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "de", name: "German" },
  { code: "hu", name: "Hungarian" },
  { code: "es", name: "Spanish" },
];

const QuestionEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dataLoaded, setDataLoaded] = useState(false);

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    { title: t("Questions"), url: "/admin/cms/question/list/1" },
    { title: "Edit Question", url: "" },
  ];

  const validationSchema = Yup.object().shape({
      question_type: Yup.string().required("Question type is required"),
      title: Yup.object().shape({
          en: Yup.string().trim().required("English title required"),
          de: Yup.string().trim().required("German title required"),
          hu: Yup.string().trim().required("Hungarian title required"),
          es: Yup.string().trim().required("Spanish title required"),
      }),
      options: Yup.array()
          .min(2, "At least 2 options required")
          .of(
              Yup.object().shape({
                  slug: Yup.string()
                      .trim()
                      .matches(/^[a-zA-Z0-9_-]+$/, "Slug must contain only alphanumeric characters, underscores, and hyphens")
                      .required("Option slug required"),
                  value: Yup.object().shape({
                      en: Yup.string().trim().required("English required"),
                      de: Yup.string().trim().required("German required"),
                      hu: Yup.string().trim().required("Hungarian required"),
                      es: Yup.string().trim().required("Spanish required"),
                  }),
              })
          ),
  });

  const formik = useFormik({
    initialValues: {
      question_type: "",
      title: { en: "", de: "", hu: "", es: "" },
      options: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      globalLoader(true);

      const formData = new FormData();
      formData.append("o_id", id);
      formData.append("question_type", values.question_type);
      formData.append("title", JSON.stringify(values.title));
      formData.append("options", JSON.stringify(values.options));

      try {
        const res = await EditQuestion(formData);
        if (res.success) {
          Swal.fire({ icon: "success", text: res.message, ...SWAL_SETTINGS });
          setTimeout(() => navigate("/admin/cms/question/list/1"), 1500);
        } else {
          throw new Error(handleServerValidations(res));
        }
      } catch (err) {
        Swal.fire({ icon: "error", text: err.message || "Failed to update", ...SWAL_SETTINGS });
      } finally {
        globalLoader(false);
      }
    },
  });

  // Load existing data
  useEffect(() => {
    if (!id) return;

    globalLoader(true);
    QuestionDetails(id)
      .then((res) => {
        if (res.success && res.data) {
          const q = res.data;

          // Parse title and options if stored as JSON
          const parsedTitle = typeof q.title === "string" ? JSON.parse(q.title) : q.title || {};
          const parsedOptions = typeof q.options === "string" ? JSON.parse(q.options) : q.options || [];

          formik.setValues({
            question_type: q.question_type || "",
            title: {
              en: parsedTitle.en || "",
              de: parsedTitle.de || "",
              hu: parsedTitle.hu || "",
              es: parsedTitle.es || "",
            },
            options: parsedOptions.length > 0
              ? parsedOptions.map(opt => ({
                  slug: opt.slug || "",
                  value: {
                    en: opt.value?.en || "",
                    de: opt.value?.de || "",
                    hu: opt.value?.hu || "",
                    es: opt.value?.es || "",
                  },
                }))
              : [{ slug: "", value: { en: "", de: "", hu: "", es: "" } }],
          });
        }
        setDataLoaded(true);
      })
      .catch((err) => {
        console.error(err);
        Swal.fire({ icon: "error", text: "Failed to load question", ...SWAL_SETTINGS });
      })
      .finally(() => globalLoader(false));
  }, [id]);

  const addOption = () => {
    formik.setFieldValue("options", [
      ...formik.values.options,
      { slug: "", value: { en: "", de: "", hu: "", es: "" } },
    ]);
  };

  const removeOption = (index) => {
    if (formik.values.options.length > 2) {
      const newOpts = formik.values.options.filter((_, i) => i !== index);
      formik.setFieldValue("options", newOpts);
    }
  };

  if (!dataLoaded) return <Loader />;

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row">
          <div className="col-12">
            <div className="card custom-card">
              <div className="card-body">
                <h5 className="mb-4">Edit Question (4 Languages)</h5>

                {/* Question Type */}
                <div className="mb-4">
                  <label>Question Type <span className="text-danger">*</span></label>
                  <select
                    className="form-control"
                    {...formik.getFieldProps("question_type")}
                  >
                    <option value="">Select Type</option>
                    {questionType.map((qt) => (
                      <option key={qt.value} value={qt.value}>
                        {t(qt.name)}
                      </option>
                    ))}
                  </select>
                  <small className="text-danger">{formik.errors.question_type}</small>
                </div>

                {/* Title in 4 Languages */}
                <div className="border p-4 rounded mb-4 bg-light">
                  <h6>Question Title (Edit in all 4 languages)</h6>
                  <div className="row">
                    {LANGUAGES.map((lang) => (
                      <div key={lang.code} className="col-md-6 mb-3">
                        <label>{lang.name} <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={formik.values.title[lang.code]}
                          onChange={(e) =>
                            formik.setFieldValue(`title.${lang.code}`, e.target.value)
                          }
                        />
                        <small className="text-danger">
                          {formik.touched.title?.[lang.code] && formik.errors.title?.[lang.code]}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <div>
                  <h6>Options (Minimum 2)</h6>
                  {formik.values.options.map((option, index) => (
                    <div key={index} className="border p-4 rounded mb-4 bg-white">
                      <div className="row align-items-end">
                        <div className="col-md-3">
                          <label>Option Slug</label>
                          <input
                            type="text"
                            className="form-control"
                            value={option.slug}
                            onChange={(e) =>
                              formik.setFieldValue(`options[${index}].slug`, e.target.value)
                            }
                          />
                          <small className="text-danger">
                            {formik.errors.options?.[index]?.slug}
                          </small>
                        </div>

                        {LANGUAGES.map((lang) => (
                          <div key={lang.code} className="col-md-2">
                            <label>{lang.name}</label>
                            <input
                              type="text"
                              className="form-control"
                              value={option.value[lang.code]}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `options[${index}].value.${lang.code}`,
                                  e.target.value
                                )
                              }
                            />
                            <small className="text-danger">
                              {formik.errors.options?.[index]?.value?.[lang.code]}
                            </small>
                          </div>
                        ))}

                        <div className="col-md-1">
                          {formik.values.options.length > 2 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => removeOption(index)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={addOption}>
                    + Add Option
                  </button>
                </div>

                {/* Submit */}
                <div className="mt-5">
                  <button type="submit" className="btn btn-primary me-3">
                    Update Question
                  </button>
                  <button type="button" onClick={() => navigate(-1)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default QuestionEdit;